import Axios, { AxiosRequestConfig } from "axios";
import currentEnv from "../config/environment";
import {
  getDataFromSessionStorage,
  removeAllDataFromSessionStorage,
  saveDataToSessionStorage,
} from "./storageService";
import { MixPanelService } from "./mixpanelService";
import { extractMessage } from "./utilService";
import { isProtectedAPI } from "../config/protectedApiList";

const axiosConfig = {
  baseURL: currentEnv.baseURL(),
  headers: {
    "Content-Type": "Application/json",
    "x-user-type": "provider",
  },
};
("");

export const getAccessToken = () => getDataFromSessionStorage("access-token");
export const getRefreshToken = () => getDataFromSessionStorage("refresh-token");

export const getTurnstileToken = async (): Promise<string | null> => {
  try {
    // @ts-expect-error - window.turnstile is injected by Cloudflare
    if (typeof window !== "undefined" && window.turnstile) {
      // Always generate a fresh token for each API call (no caching)
      // @ts-expect-error - custom property for widget ID
      const widgetId = window.turnstileWidgetId;
      if (widgetId !== undefined) {
        // @ts-expect-error - window.turnstile.execute is from Cloudflare SDK
        await window.turnstile.execute(widgetId);

        // In invisible mode, get the token that was just generated via getResponse()
        // @ts-expect-error - window.turnstile.getResponse is from Cloudflare SDK
        const token = window.turnstile.getResponse(widgetId);

        if (token) {
          return token;
        }

        // Fallback: wait for callback to set token (in case getResponse returns empty)
        let attempts = 0;
        const maxAttempts = 10; // 1 second max wait (10 * 100ms)

        while (attempts < maxAttempts) {
          // @ts-expect-error - custom property
          if (window.turnstileToken) {
            // @ts-expect-error - custom property
            const callbackToken = window.turnstileToken;
            // Clear from window after retrieving
            // @ts-expect-error - custom property
            window.turnstileToken = null;
            return callbackToken;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
          attempts++;
        }
        return null;
      }
      return null;
    }
    return null;
  } catch {
    return null;
  }
};

function getApiConfig({
  headers = {},
  appConfig = {},
  doNotNeedAuthorizationHeader = false,
}) {
  const mainConfig: any = {
    ...axiosConfig,
    ...appConfig,
    headers: {
      ...axiosConfig.headers,
      ...headers,
      "time-zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      "x-platform": "web",
    },
  };

  mainConfig.baseURL = axiosConfig.baseURL;
  if (!doNotNeedAuthorizationHeader)
    mainConfig.headers["Authorization"] = getAccessToken();
  return mainConfig;
}

const ApiCall = async (ajaxParams: AxiosRequestConfig<any>) => {
  // Check if this is a protected API
  const url = ajaxParams.url || "";
  const method = ajaxParams.method || "GET";

  if (isProtectedAPI(url, method)) {
    const turnstileToken = await getTurnstileToken();
    if (turnstileToken) {
      if (!ajaxParams.headers) {
        ajaxParams.headers = {};
      }
      ajaxParams.headers["x-attestation-token"] = turnstileToken;
    }
  }

  return Axios(ajaxParams);
};

export function delay() {
  return new Promise(() => {});
}

export const GET = ({ url = "", params = {}, headers = {}, signal }: any) => {
  if (!url) throw new Error("Please specify a API URL");
  const config = getApiConfig({ headers });
  const ajaxParams = {
    ...config,
    url,
    params,
    method: "GET",
    signal,
  };

  return ApiCall(ajaxParams);
};

export const APICall = ({
  method = "",
  url = "",
  baseUrl = "",
  params = {},
  headers = {},
  signal,
}: any) => {
  if (!url) throw new Error("Please specify a API URL");
  const config = getApiConfig({
    headers,
    appConfig: { baseURL: baseUrl, fetchBaseUrl: true },
  });
  const ajaxParams = {
    ...config,
    url,
    params,
    method,
    signal,
  };

  return ApiCall(ajaxParams);
};

export const GET_FILE = ({
  url = "",
  params = {},
  headers = {},
  signal,
}: any) => {
  if (!url) throw new Error("Please specify a API URL");
  const config = getApiConfig({ headers });
  const ajaxParams: any = {
    ...config,
    url,
    params,
    method: "GET",
    responseType: "arraybuffer",
    signal,
  };

  return ApiCall(ajaxParams);
};

export const POST = ({
  url = "",
  params = {},
  data = {},
  headers = {},
  appConfig = {},
  doNotNeedAuthorizationHeader = false,
  signal,
}: any) => {
  if (!url) throw new Error("Please specify a API URL");

  const config = getApiConfig({
    headers,
    appConfig,
    doNotNeedAuthorizationHeader,
  });

  const ajaxParams = {
    ...config,
    url,
    data,
    params,
    method: "POST",
    signal,
  };

  return ApiCall(ajaxParams);
};

export const PUT = ({
  url = "",
  params = {},
  data = {},
  headers = {},
  signal,
}: any) => {
  if (!url) throw new Error("Please specify a API URL");

  const config = getApiConfig({ headers });

  const ajaxParams = {
    ...config,
    url,
    data,
    params,
    method: "PUT",
    signal,
  };

  return ApiCall(ajaxParams);
};

export const PATCH = ({
  url = "",
  params = {},
  data = {},
  headers = {},
  signal,
}: any) => {
  if (!url) throw new Error("Please specify a API URL");

  const config = getApiConfig({ headers });

  const ajaxParams = {
    ...config,
    url,
    data,
    params,
    method: "PATCH",
    signal,
  };

  return ApiCall(ajaxParams);
};

export const DELETE = ({
  url = "",
  params = {},
  data = {},
  headers = {},
  appConfig = {},
  signal,
}: any) => {
  if (!url) throw new Error("Please specify a API URL");

  const config = getApiConfig({ headers, appConfig });

  const ajaxParams = {
    ...config,
    url,
    data,
    params,
    method: "DELETE",
    signal,
  };

  return ApiCall(ajaxParams);
};

function responseInterceptorFunction() {
  const responseInterceptor = Axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const originalRequest = (error && error.config) || {};
      const status = (error && error.response && error.response.status) || 0;

      if (status === 401) {
        Axios.interceptors.response.eject(responseInterceptor);

        return POST({
          url: "accounts/refresh",
          data: { token: getRefreshToken() },
        })
          .then((response) => {
            const { data: { accessToken, refreshToken } = {} } = response;
            saveDataToSessionStorage("access-token", accessToken);
            saveDataToSessionStorage("refresh-token", refreshToken);
            originalRequest.headers.Authorization = `${accessToken}`;
            return Axios(originalRequest);
          })
          .catch((error) => {
            if (
              error.config &&
              error.config.url.includes("accounts/refresh") &&
              typeof window !== "undefined"
            ) {
              removeAllDataFromSessionStorage();
              if (typeof window !== "undefined") {
                window.location.href = "/";
                return;
              }
            }
          })
          .finally(responseInterceptorFunction);
      }
      MixPanelService.track("api_error", {
        status_code: status,
        error_description: extractMessage(error),
        api_url: error?.response?.config?.url,
      });
      if (
        (status === 406 ||
          (status === 400 &&
            error.config &&
            error.config.url.includes("accounts/refresh")) ||
          (status === 400 &&
            error.response &&
            error.response.config.url.includes("accounts/refresh"))) &&
        typeof window !== "undefined"
      ) {
        removeAllDataFromSessionStorage();
        if (typeof window !== "undefined") {
          window.location.href = "/";
          return;
        }
      }

      return Promise.reject(error);
    }
  );
}

responseInterceptorFunction();
